import React, { useContext, useEffect, useMemo, useState } from 'react'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'

//@MUI
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'

import EmptyState from '../../../components/EmptyState/EmptyState'
import TaskPane from '../../../components/Tasks/TaskPane'
import useIsMobile from '../../../helpers/useIsMobile'
import AddNewTask from './AddNewTask'
import TaskBoard from './TaskBoard'
import TaskFocusRail from './TaskFocusRail'
import TaskListView from './TaskListView'
import {
  getDueMeta,
  getLinkedRecord,
  isAssignedToMe,
  normaliseTask,
  sortTasks,
} from './taskHelpers'

const COMPLETED_WINDOW_DAYS = 7

const MyTasks = () => {
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const isMobile = useIsMobile()

  const [tasks, setTasks] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  // Completed tasks stay out of the way unless the user switches them on
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [scope, setScope] = useState('all') // 'all' | 'me' | 'team:<name>'
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('dueDate')
  const [view, setView] = useState('board')
  const [selectedTask, setSelectedTask] = useState(null)
  const [openCreateNew, setOpenCreateNew] = useState(false)

  const loadTasks = () => {
    setLoading(true)
    setFetchError(false)
    API.get(
      `/my-tasks?includeCompleted=${includeCompleted}&completedDays=${COMPLETED_WINDOW_DAYS}`,
    )
      .then((res) => {
        if (res.status !== 200) return setFetchError(true)
        // A task assigned to me can also match one of my teams — keep one copy
        const byId = new Map()
        ;[...res.data.assignedToMe, ...res.data.assignedToTeam].forEach(
          (task) => byId.set(task._id, normaliseTask(task)),
        )
        setTasks([...byId.values()])
        setTeams(res.data.userTeams ?? [])
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }
  useEffect(() => loadTasks(), [includeCompleted])

  const openTasks = useMemo(
    () => tasks.filter((task) => task.taskStatus !== 'Completed'),
    [tasks],
  )

  // "Today" panel — always across everything, whatever filter is active
  const focus = useMemo(() => {
    const result = { overdue: 0, today: 0, week: 0, oldestOverdueDays: 0 }
    openTasks.forEach((task) => {
      const due = getDueMeta(task)
      if (due.bucket in result) result[due.bucket] += 1
      if (due.bucket === 'overdue')
        result.oldestOverdueDays = Math.max(
          result.oldestOverdueDays,
          due.daysOverdue,
        )
    })
    return result
  }, [openTasks])

  const counts = useMemo(
    () => ({
      all: tasks.length,
      me: tasks.filter((task) => isAssignedToMe(task, user._id)).length,
      teams: teams
        .map((name) => ({
          name,
          count: tasks.filter((task) => task.assignedToTeam === name).length,
        }))
        .filter((team) => team.count > 0),
    }),
    [tasks, teams, user._id],
  )

  const visibleTasks = useMemo(() => {
    const query = search.trim().toLowerCase()
    const inScope = (task) => {
      if (scope === 'me') return isAssignedToMe(task, user._id)
      if (scope.startsWith('team:'))
        return task.assignedToTeam === scope.slice(5)
      return true
    }
    const matches = (task) => {
      if (!query) return true
      const linked = getLinkedRecord(task)
      return [
        task.taskName,
        task.assignedToTeam,
        linked?.label,
        linked?.type,
        task.assignedToUser &&
          `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`,
      ].some((value) => value?.toLowerCase().includes(query))
    }
    return sortTasks(tasks.filter((task) => inScope(task) && matches(task)), sortBy)
  }, [tasks, scope, search, sortBy, user._id])

  // Optimistic status change from the board; revert if the save fails
  const changeStatus = (taskId, taskStatus) => {
    const previous = tasks
    setTasks((prev) =>
      prev.map((task) =>
        task._id === taskId
          ? { ...task, taskStatus, updatedAt: new Date().toISOString() }
          : task,
      ),
    )
    API.put(`/task/${taskId}`, { taskStatus })
      .then((res) => {
        if (res.status === 200) setToast(true)
        else setTasks(previous)
      })
      .catch(() => setTasks(previous))
  }

  const closeDrawer = () => setSelectedTask(null)

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={1.5}
      >
        <Box>
          <Typography variant="title2">Tasks</Typography>
          <Typography variant="smallGrey" component="div">
            Everything assigned to you and to your teams, in one board
          </Typography>
        </Box>
        <Stack direction="row" gap={1.5} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(e, value) => value && setView(value)}
            sx={{ bgcolor: 'background.paper' }}
          >
            <ToggleButton value="board" sx={{ px: 1.75, gap: 0.75 }}>
              <ViewKanbanOutlinedIcon fontSize="small" />
              Board
            </ToggleButton>
            <ToggleButton value="list" sx={{ px: 1.75, gap: 0.75 }}>
              <ViewListOutlinedIcon fontSize="small" />
              List
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateNew(true)}
            sx={{ ml: { xs: 'auto', md: 0 } }}
          >
            New task
          </Button>
        </Stack>
      </Stack>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">
          Unable to load your tasks. Please try again or contact AmbuNet
          support.
        </Alert>
      )}

      {!loading && !fetchError && tasks.length === 0 ? (
        <EmptyState
          icon={AssignmentTurnedInOutlinedIcon}
          title="You're all caught up"
          description="Nothing is assigned to you or your teams right now. Tasks raised against events, incidents, defects and safety alerts land here, as do any you create yourself."
          actions={[{ label: 'New task', onClick: () => setOpenCreateNew(true) }]}
        />
      ) : (
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={2}
          alignItems="stretch"
        >
          <TaskFocusRail
            focus={focus}
            counts={counts}
            scope={scope}
            onScopeChange={setScope}
            includeCompleted={includeCompleted}
            onIncludeCompletedChange={setIncludeCompleted}
          />
          <Stack flex={1} minWidth={0} gap={1.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1.25}
              alignItems={{ sm: 'center' }}
            >
              <TextField
                size="small"
                placeholder="Search tasks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { sm: 260 }, bgcolor: 'background.paper' }}
              />
              <TextField
                select
                size="small"
                label="Sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ width: { sm: 170 }, bgcolor: 'background.paper' }}
              >
                <MenuItem value="dueDate">Due date</MenuItem>
                <MenuItem value="priority">Priority</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
              </TextField>
              {view === 'board' && !isMobile && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ ml: 'auto' }}
                >
                  Drag a card between columns to change its status
                </Typography>
              )}
            </Stack>

            {visibleTasks.length === 0 && !loading ? (
              <Alert severity="info">
                No tasks match{search ? ' this search' : ' this filter'}.
              </Alert>
            ) : view === 'board' ? (
              <TaskBoard
                tasks={visibleTasks}
                currentUserId={user._id}
                showingCompleted={includeCompleted}
                onOpen={setSelectedTask}
                onStatusChange={changeStatus}
              />
            ) : (
              <TaskListView
                tasks={visibleTasks}
                currentUserId={user._id}
                onOpen={setSelectedTask}
              />
            )}
          </Stack>
        </Stack>
      )}

      <Modal open={openCreateNew} onClose={() => setOpenCreateNew(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100vw - 24px)', md: 820 },
            maxHeight: '92vh',
            overflowY: 'auto',
            outline: 'none',
          }}
        >
          <AddNewTask
            onClose={() => setOpenCreateNew(false)}
            successAction={() => {
              setOpenCreateNew(false)
              loadTasks()
            }}
          />
        </Box>
      </Modal>

      <Drawer open={!!selectedTask} onClose={closeDrawer} anchor="right">
        <TaskPane
          taskID={selectedTask}
          onClose={closeDrawer}
          onComplete={() => {
            closeDrawer()
            loadTasks()
          }}
        />
      </Drawer>
    </Stack>
  )
}

export default MyTasks
